// When messages a case's story cites were said, and who said them, read from
// the trace's record: a time where the walkthrough kept none (one written
// before notes carried times, or a message it knew only by id), and whether a
// step's message was the person's or the agent's. Each is read as the
// record's page holding that message (conversationRead's aroundMessageId), so
// where it sits in a long trace does not matter: its own time, else when
// Origin first received it (`approx`). Kept for the page's life; a failed
// read is asked again.
import { useEffect, useState } from "react";
import { readConversationPage } from "./conversationRead.js";
import { recordWindow } from "./triageTimeline.js";
import { typedByPerson } from "./findingStory.js";

const lower = (v) => String(v ?? "").trim().toLowerCase();
// Reads in flight at once, for one story.
const AT_ONCE = 3;
// `${session}|${message}` -> { at, approx, source } | null (read, and not found).
const kept = new Map();
const keyOf = (session, messageId) => `${lower(session)}|${lower(messageId)}`;
export function _clearRecordTimesForTests() { kept.clear(); }

// Who a message is from: words the person typed, or the agent's (its reply,
// its call, or what came back from the call); null for anything else, such as
// a notice sent in the person's role.
function sourceOf(turn) {
    if (typedByPerson(turn)) return "person";
    if (turn.role === "assistant" || (turn.blocks || []).some((b) => b?.type === "tool_result")) return "agent";
    return null;
}

/** -> Promise<{ at, approx, source } | null>: when `messageId` was said and by whom, as the record keeps it. */
export async function readRecordTime({ session, messageId, signal = null, readPage = readConversationPage }) {
    const page = await readPage({ traceId: lower(session), timeRange: recordWindow(), aroundMessageId: lower(messageId), maxMessages: 1, signal });
    const turn = (page?.turns || []).find((t) => lower(t.messageId) === lower(messageId));
    if (!turn) return null;
    const source = sourceOf(turn);
    if (Number.isFinite(turn.messageAt)) return { at: turn.messageAt, approx: false, source };
    if (Number.isFinite(turn.firstReceivedAt)) return { at: turn.firstReceivedAt, approx: true, source };
    return source ? { at: null, approx: false, source } : null;
}

/**
 * When each of `messageIds` in `session` was said and by whom, as Map(lower
 * id -> { at, approx, source }), filled as the reads land. Only messages not yet read are
 * read.
 */
export function useRecordTimes(session, messageIds) {
    const s = lower(session);
    const ids = [...new Set((messageIds || []).map(lower).filter(Boolean))];
    // Keyed by what is asked, not what is left, so a read landing does not
    // cancel the others.
    const idsKey = ids.join(",");
    const [, setTick] = useState(0);
    useEffect(() => {
        const queue = s && idsKey ? idsKey.split(",").filter((id) => !kept.has(keyOf(s, id))) : [];
        if (!queue.length) return undefined;
        const ctrl = new AbortController();
        const next = async () => {
            for (let id = queue.shift(); id && !ctrl.signal.aborted; id = queue.shift()) {
                try {
                    kept.set(keyOf(s, id), await readRecordTime({ session: s, messageId: id, signal: ctrl.signal }));
                    if (!ctrl.signal.aborted) setTick((n) => n + 1);
                } catch { /* asked again the next time the story is drawn */ }
            }
        };
        for (let i = 0; i < AT_ONCE; i += 1) next();
        return () => ctrl.abort();
    }, [s, idsKey]);
    const out = new Map();
    for (const id of ids) {
        const when = kept.get(keyOf(s, id));
        if (when) out.set(id, when);
    }
    return out;
}
